import { forwardRef, useCallback, useRef } from 'react'
import { Link, useLocation, useNavigate, type LinkProps, type NavigateFunction, type NavigateOptions, type To } from 'react-router-dom'
import { HOME_ROUTE, homeWorkspaceCloseDelta, isHomeDashboardPath, isHomeWorkspacePath, workspaceDestination, type HomeWorkspaceState } from './homeWorkspace'

const historyIndex = () => typeof window.history.state?.idx === 'number' ? window.history.state.idx as number : null

function navigationState(location: ReturnType<typeof useLocation>, to: To, state: unknown, replace = false) {
  const pathname = typeof to === 'string' ? to.split(/[?#]/)[0] : to.pathname || location.pathname
  if (!isHomeWorkspacePath(location.pathname) || !isHomeWorkspacePath(pathname)) return state
  const current = location.state as HomeWorkspaceState | null
  const previous = { url: location.pathname + location.search + location.hash, index: historyIndex() }
  return {
    ...(state && typeof state === 'object' ? state : {}),
    homeWorkspace: replace && current?.homeWorkspace ? current.homeWorkspace : {
      previous,
      background: current?.homeWorkspace?.background || (isHomeDashboardPath(location.pathname) ? previous : { url: HOME_ROUTE, index: null }),
    },
  }
}

export const WorkspaceLink = forwardRef<HTMLAnchorElement, LinkProps>(function WorkspaceLink({ to, state, replace, ...props }, ref) {
  const location = useLocation()
  const destination = workspaceDestination(to, location.pathname)
  return <Link {...props} ref={ref} to={destination} replace={replace} state={navigationState(location, destination, state, replace)} />
})

export function useWorkspaceNavigate(): NavigateFunction {
  const navigate = useNavigate()
  const location = useLocation()
  return useCallback(((to: To | number, options?: NavigateOptions) => {
    if (typeof to === 'number') return navigate(to)
    const destination = workspaceDestination(to, location.pathname)
    return navigate(destination, { ...options, state: navigationState(location, destination, options?.state, options?.replace) })
  }) as NavigateFunction, [navigate, location])
}

export function useCloseHomePanel() {
  const navigate = useNavigate()
  const location = useLocation()
  // Query-string tab changes must not lose the panel's original return point.
  const entry = useRef((location.state as HomeWorkspaceState | null)?.homeWorkspace)
  return useCallback(() => {
    const delta = homeWorkspaceCloseDelta(historyIndex(), entry.current?.previous)
    if (delta !== null) navigate(delta)
    else navigate(HOME_ROUTE, { replace: true })
  }, [navigate])
}
